import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SearchBox,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { NavigationRegular, SignOutRegular } from '@fluentui/react-icons';
import { useAuth } from '@worklogix/web-common';

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    height: '48px',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground5,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  wordmark: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  search: {
    flex: 1,
    maxWidth: '520px',
  },
  searchBox: {
    width: '100%',
  },
  spacer: {
    flex: 1,
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

interface CommandBarProps {
  onToggleRail: () => void;
  railCollapsed: boolean;
}

/**
 * Top command bar: rail toggle, WorkLogix wordmark, a global search field
 * (stub, non-functional this phase) and the signed-in user menu with sign-out.
 */
export function CommandBar({ onToggleRail, railCollapsed }: CommandBarProps): JSX.Element {
  const styles = useStyles();
  const { displayName, logout } = useAuth();
  const name = displayName ?? 'Signed in';

  return (
    <header className={styles.bar}>
      <Button
        appearance="subtle"
        icon={<NavigationRegular />}
        aria-label={railCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-pressed={!railCollapsed}
        onClick={onToggleRail}
      />
      <Text as="span" className={styles.wordmark}>
        WorkLogix
      </Text>

      <div className={styles.search}>
        <SearchBox
          appearance="filled-darker"
          placeholder="Search resources (coming soon)"
          disabled
          aria-label="Global search"
          className={styles.searchBox}
        />
      </div>

      <div className={styles.spacer} />

      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button appearance="transparent" aria-label={`Account menu for ${name}`}>
            <span className={styles.user}>
              <Avatar name={name} size={28} color="colorful" />
              <Text>{name}</Text>
            </span>
          </Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<SignOutRegular />} onClick={() => void logout()}>
              Sign out
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </header>
  );
}
